import FAQSchema from './schemas/faq.json';
import IndexSchema from './schemas/home.json';
import { Bundle, Item } from 'types';

export const JsonLdScript = (schema: DynamicObject) => `<script type="application/ld+json">${JSON.stringify(schema)}</script>`;

const schemas = [
  {
    pathname: '/',
    schema: IndexSchema,
  },
  {
    pathname: '/support/faq',
    schema: FAQSchema,
  },
];

export const getPageSchema = (pathname: string) => {
  return schemas.find((p) => p.pathname === pathname)?.schema ?? null;
};

export const getSubcategoryPageSchema = (name: string, url: string, bundles: Item[]) => {
  const itemListElement = bundles.map((bundle, index) => {
    const heroImage = bundle.media?.find((m) => m.label?.toLowerCase() === 'pdp_hero');

    if (!heroImage?.url) {
      throw new Error(`Unable to build JSON-LD schema: Missing PDP_Hero image for bundle (ID: ${bundle.id})`);
    }

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: bundle.display_name,
      url: `${process.env.NEXT_PUBLIC_LOCAL_URL}/${bundle.url_slug}`,
      image: `${heroImage.url}?auto=format,compress&fit=crop&w=308&ar=5:7`,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    mainEntity: {
      '@type': 'ItemList',
      name,
      url,
      itemListElement,
    },
  };
};

const isBundle = (b: any): b is Bundle => ['suit', 'tuxedo'].includes(b.type);

const getItemImages = (item: Item): string[] => [
  `${item.media?.find((m) => m.label?.toLowerCase() === 'pdp_hero')?.url}?fm=jpg&fit=${
    isBundle(item) ? 'crop' : 'max'
  }&w=400&ar=5:7&auto=format`,
];

export const getItemPageSchema = async (item: Item, url: string) => {
  try {
    const itemPageSchema: DynamicObject = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: item.display_name,
      image: getItemImages(item),
      description: item.short_description,
      brand: {
        '@type': 'Brand',
        name: 'Generation Tux',
      },
      offers: {
        '@type': 'Offer',
        url: `${process.env.NEXT_PUBLIC_LOCAL_URL}${url}`,
        priceCurrency: 'USD',
        price: item.cost,
        availability: 'https://schema.org/InStock',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'US',
          },
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            name: 'Generation Tux Rental Return Policy',
            returnPolicyCategory: 'https://schema.org/MerchantReturnByMail',
            merchantReturnDays: 3,
            returnFees: 'https://schema.org/FreeReturn',
            returnMethod: 'https://schema.org/ReturnByMail',
            description:
              'Rental items must be dropped off at a UPS location within 3 days after the event. Late fees of $25/day apply after this period.',
          },
        },
      },
    };

    if (isBundle(item) && item.productReviews?.reviews.length) {
      itemPageSchema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: item.productReviews.bottomLine.averageScore.toFixed(2),
        ratingCount: item.productReviews.bottomLine.totalReview.toString(),
      };
    }

    return itemPageSchema;
  } catch (e) {
    throw new Error(`Error retrieving item page schema: ${e}`);
  }
};
